<script lang="ts" setup>
  import type { Restaurant } from '@/types/api/restaurant'

  type PlacePrediction = {
    placeId: string
    label: string
  }

  type LatLng = {
    lat: number
    lng: number
  }

  const emit = defineEmits<{
    (event: 'restaurants', restaurants: Restaurant[]): void
  }>()

  const geolocation = useGeolocation({ enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 })
  const { errorText } = useError(geolocation)
  const { getPlacePredictions, getLatLngOfPlace } = useGeocoder()
  const {
    resultItemRefs,
    resultKeydownTabHandler,
    resultKeydownUpArrowHandler,
    resultKeydownDownArrowHandler,
    resultKeydownShiftTabHandler
  } = useResultItemHandlers()
  const {
    searchInputHandler,
    searchInputKeydownDownHandler,
    searchInputKeydownEscHandler,
    searchInputKeydownEnterHandler
  } = useSearchInputHandlers()

  const isSearching = ref<boolean>(false)
  const searchText = ref<string>('')
  const searchResults = ref<PlacePrediction[]>([])
  const selectedLocation = ref<LatLng | null>(null)

  /**
   * @description Clear the search results to dismiss the dropdown
   */
  function clearResults() {
    searchResults.value = []
  }

  /**
   * @description Get nearby restaurants from the API
   * @param lat {number}
   * @param lng {number}
   */
  async function getNearbyRestaurants(lat: number, lng: number): Promise<Restaurant[]> {
    try {
      isSearching.value = true

      const $config = useRuntimeConfig()

      const restaurants = await $fetch<Restaurant[] | null>(
        $config.public.apiBaseUrl + `/Restaurants/nearby?lat=${lat}&lng=${lng}&limit=10`
      )

      if (restaurants === null) {
        return []
      }

      return restaurants.filter((restaurant) => {
        if (restaurant.distanceInMetres === null) {
          return false
        }

        return restaurant.distanceInMetres <= 70000
      })
    } catch (error: any) {
      console.error('Error fetching nearby restaurants: ', error)
      return []
    } finally {
      isSearching.value = false
    }
  }

  /**
   * @description When a place prediction is selected, get the latitude and longitude of the place
   * @param prediction {PlacePrediction}
   */
  async function onPredictionSelected(prediction: PlacePrediction) {
    isSearching.value = true
    searchResults.value = []
    searchText.value = prediction.label
    selectedLocation.value = await getLatLngOfPlace(prediction.placeId)
    selectedLocationIsGeolocated.value = false
    isSearching.value = false
  }

  /**
   * @description Clear the search results if the input is empty
   */
  watch(searchText, () => {
    if (searchText.value === '') {
      clearResults()
    }
  })

  /**
   * @description Watch for changes in the selected location and get nearby Grill'd restaurants
   */
  watch(selectedLocation, async () => {
    if (selectedLocation.value === null) {
      return
    }

    const nearbyRestaurants = await getNearbyRestaurants(selectedLocation.value.lat, selectedLocation.value.lng)

    emit('restaurants', nearbyRestaurants)
  })

  function useMyLocationClicked() {
    isSearching.value = true
    locationHasBeenExplicitlyRequested.value = true
    geolocation.resume()
    isSearching.value = false
  }

  // this prevents the location error appearing before the user has explicitly requested location
  const locationHasBeenExplicitlyRequested = ref<boolean>(false)

  // this prevents duplication of API calls
  const selectedLocationIsGeolocated = ref<boolean | null>(null)

  watch(geolocation.coords, () => {
    // pause the geolocation to prevent further updates until requested
    geolocation.pause()
    if (selectedLocationIsGeolocated.value === true) {
      return
    }

    selectedLocation.value = {
      lat: geolocation.coords.value.latitude,
      lng: geolocation.coords.value.longitude
    }
    selectedLocationIsGeolocated.value = true
  })

  /**
   * @description Use the Google Maps AutocompleteService and Geocoder
   */
  function useGeocoder() {
    const autoCompleteService = ref<google.maps.places.AutocompleteService>()
    const geocoder = ref<google.maps.Geocoder>()

    onMounted(() => {
      autoCompleteService.value = new google.maps.places.AutocompleteService()
      geocoder.value = new google.maps.Geocoder()
    })

    /**
     * @description Get place predictions from the Google Maps AutocompleteService
     * @param searchText
     */
    async function getPlacePredictions(searchText: string): Promise<PlacePrediction[]> {
      if (searchText === '') {
        return []
      }
      const searchQuery = {
        input: searchText,
        componentRestrictions: {
          country: 'au'
        }
      }

      const predictions = (await autoCompleteService.value!.getPlacePredictions(searchQuery)).predictions

      return predictions.map((x) => ({
        placeId: x.place_id,
        label:
          x.structured_formatting.main_text + ', ' + x.structured_formatting.secondary_text?.replace(/, Australia$/, '')
      }))
    }

    /**
     * @description Get the latitude and longitude of a place from the Google Maps Geocoder
     * @param placeId {string} - The place ID of the location
     */
    async function getLatLngOfPlace(placeId: string): Promise<LatLng> {
      if (!geocoder.value) {
        throw new Error('Geocoder not initialized')
      }

      const geocodeResponse = await geocoder.value.geocode({ placeId })
      const location = geocodeResponse.results[0].geometry.location
      return {
        lat: location.lat(),
        lng: location.lng()
      }
    }

    return {
      getPlacePredictions,
      getLatLngOfPlace
    }
  }

  /**
   * @description Display an error message when the geolocation API returns an error
   * @param geolocation - The geolocation composable
   */
  function useError(geolocation: ReturnType<typeof useGeolocation>) {
    const errorText = ref<string>('')

    watch(geolocation.error, () => {
      if (geolocation.error.value?.PERMISSION_DENIED === 1 && locationHasBeenExplicitlyRequested.value) {
        errorText.value = 'Please check your browser settings to update your location preferences.'
      } else {
        errorText.value = ''
      }
      // Pause the geolocation to prevent further updates until requested
      geolocation.pause()
    })

    return {
      errorText: readonly(errorText)
    }
  }

  /**
   * @description Keyboard event handlers for the search input
   */
  function useSearchInputHandlers() {
    /**
     * @description When the input changes,
     * get the place predictions from the Google Maps AutocompleteService
     */
    async function searchInputHandler() {
      searchResults.value = await getPlacePredictions(searchText.value)
    }

    /**
     * @description When the user presses down,
     * focus the first prediction if there are any
     */
    function searchInputKeydownDownHandler() {
      if (searchResults.value.length) {
        resultItemRefs.value[0].focus()
      }
    }

    /**
     * @description When the user presses enter,
     * select the first prediction
     */
    async function searchInputKeydownEnterHandler() {
      if (!searchResults.value.length) {
        return
      }

      await onPredictionSelected(searchResults.value[0])
    }

    /**
     * @description When the user presses esc,
     * clear the search results
     */
    function searchInputKeydownEscHandler() {
      clearResults()
    }

    return {
      searchInputHandler,
      searchInputKeydownDownHandler,
      searchInputKeydownEnterHandler,
      searchInputKeydownEscHandler
    }
  }

  /**
   * @description Keyboard event handlers for the search results
   */
  function useResultItemHandlers() {
    const resultItemRefs = ref<Record<string, any>>({})

    /**
     * @description When the user presses tab when the last result is focused,
     * focus the first result
     */
    function resultKeydownTabHandler(event: Event, index: number) {
      if (index === searchResults.value.length - 1) {
        event.preventDefault()
        resultItemRefs.value[0].focus()
      }
    }

    /**
     * @description When the user presses shift + tab when the first result is focused,
     * focus the last result
     */
    function resultKeydownShiftTabHandler(event: Event, index: number) {
      if (index === 0) {
        event.preventDefault()
        resultItemRefs.value[searchResults.value.length - 1].focus()
      }
    }

    /**
     * @description When the user presses up arrow,
     * focus the previous result
     */
    function resultKeydownUpArrowHandler(index: number) {
      if (index === 0) {
        resultItemRefs.value[searchResults.value.length - 1].focus()
      } else {
        resultItemRefs.value[index - 1].focus()
      }
    }

    /**
     * @description When the user presses down arrow,
     * focus the next result
     */
    function resultKeydownDownArrowHandler(index: number) {
      if (index === searchResults.value.length - 1) {
        resultItemRefs.value[0].focus()
      } else {
        resultItemRefs.value[index + 1].focus()
      }
    }

    return {
      resultItemRefs,
      resultKeydownTabHandler,
      resultKeydownShiftTabHandler,
      resultKeydownUpArrowHandler,
      resultKeydownDownArrowHandler
    }
  }
</script>

<template>
  <div class="controls-wrapper">
    <input
      v-model="searchText"
      aria-label="Enter your location"
      placeholder="Enter your location"
      type="search"
      @input="searchInputHandler"
      @keydown.esc="searchInputKeydownEscHandler"
      @keydown.enter="searchInputKeydownEnterHandler"
      @keydown.down.prevent="searchInputKeydownDownHandler"
    />

    <CButton
      :disabled="!!errorText"
      :tab-index="searchResults.length ? -1 : 0"
      class="geolocation-button"
      @click="useMyLocationClicked"
      @keydown.enter="useMyLocationClicked"
    >
      <div aria-hidden="true" class="geolocation-button-label-icon">
        <CSpinner v-if="isSearching" color="inherit" size="sm" />
        <icon-ionic-locate v-else class="icon-locate" height="24" width="24" />
      </div>
      <span>Use my location</span>
    </CButton>
  </div>

  <div v-if="!!errorText" class="error">
    <icon-ionic-information height="24" width="24" />
    <span>
      {{ errorText }}
    </span>
  </div>

  <div class="dropdown-menu-wrapper module-paddings">
    <ul v-show="searchResults.length" class="dropdown-menu" role="menu">
      <li
        v-for="(prediction, index) in searchResults"
        :key="prediction.placeId"
        :ref="(el) => (resultItemRefs[index] = el)"
        class="dropdown-menu-item"
        role="menuitem"
        tabindex="0"
        @click="onPredictionSelected(prediction)"
        @keydown.esc="clearResults"
        @keydown.enter="onPredictionSelected(prediction)"
        @keydown.tab.exact="resultKeydownTabHandler($event, index)"
        @keydown.shift.tab.exact="resultKeydownShiftTabHandler($event, index)"
        @keydown.up.prevent="resultKeydownUpArrowHandler(index)"
        @keydown.down.prevent="resultKeydownDownArrowHandler(index)"
      >
        <icon-ionic-location-outline aria-hidden="true" class="location-icon" height="24" width="24" />
        <span>{{ prediction.label }}</span>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
  .controls-wrapper {
    display: flex;
    flex-direction: column;
    gap: 16px;

    @include screenSmAndUp {
      flex-direction: row;
    }
  }

  input {
    flex: 1;
    width: 100%;
    outline: solid var(--color-stroke-light) 1px;
    border: none;
    border-radius: 4px;
    padding: 12px 36px 12px 12px;
    background: transparent url('../../../assets/icons/ionic/search-outline.svg?url_encode') no-repeat right 12px
      center/16px;
  }

  .error {
    margin-top: 16px;
    padding: 12px 16px;

    background-color: var(--color-secondary-red-lightest);
    color: var(--color-secondary-red-dark);

    display: flex;
    flex-direction: row;
    gap: 8px;
  }

  .geolocation-button {
    height: 48px;

    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  .dropdown-menu-wrapper {
    position: absolute;
    left: 0;
    right: 0;

    .dropdown-menu {
      box-shadow: var(--box-shadow-md);
      padding: 0;

      .dropdown-menu-item {
        display: flex;
        flex-direction: row;
        gap: 8px;
        padding: 16px;
        background-color: var(--color-base-white);
        cursor: pointer;

        &:hover,
        &:focus,
        &:active {
          background-color: var(--color-base-red);
          color: var(--color-base-white);
        }

        .location-icon {
          flex-shrink: 0;
        }
      }
    }
  }
</style>
